import React, { Component } from "react";
import Table from "../../_common/Table";
import { Button, ButtonGroup, Collapse, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { actionScripts } from "../../../_helpers/actionScripts";
import "./QSheetStyles.css";

const formatDate = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};

// Convert a signed decimal from the backend into { amount: string, sign: 1 | -1 }
const formatQty = (n) => parseFloat(n.toFixed(3)).toString();

const splitSigned = (value) => {
  const n = parseFloat(value) || 0;
  return { amount: String(Math.abs(n)), sign: n < 0 ? -1 : 1 };
};

const MODAL_EMPTY = { open: false, editIndex: null, amount: "", sign: 1, justification: "", error: "" };

class QBody extends Component {
  fileInputRef = React.createRef();

  static getDerivedStateFromProps(nextProps, prevState) {
    const initFromProps = () => ({
      _initialised: true,
      _syncNeeded: false,
      rows: nextProps.rows.map((r) => ({ ...r })),
      adjustments: nextProps.adjustments.map((a) => ({
        qSheetAdjustmentId: a.qSheetAdjustmentId,
        ...splitSigned(a.adjustmentAmount),
        justification: a.justification ?? "",
        createdByFullName: a.createdByFullName ?? null,
        lastEditedByFullName: a.lastEditedByFullName ?? null,
        createdOn: a.createdOn ?? null,
        lastEditedOn: a.lastEditedOn ?? null,
      })),
      attachments: nextProps.attachments,
      notes: nextProps.notesRemarks ?? "",
      ccoCheck: nextProps.payQuantityIncludesCcoWork ?? false,
    });

    // Switching to a different (or newly loaded) QSheet — reset local edits.
    if (nextProps.qSheetId !== null && nextProps.qSheetId !== prevState._qSheetId) {
      const base = {
        _initialised: false,
        _qSheetId: nextProps.qSheetId,
        _syncNeeded: false,
        rows: [],
        adjustments: [],
        attachments: nextProps.attachments,
        notes: nextProps.notesRemarks ?? "",
        ccoCheck: nextProps.payQuantityIncludesCcoWork ?? false,
        cco: {
          no: nextProps.bidItemCcos?.length === 1 ? String(nextProps.bidItemCcos[0].ccoNo) : "",
          amount: "",
          sign: 1,
          type: null,
        },
        adjustmentModal: MODAL_EMPTY,
      };
      if (nextProps.rows.length > 0 || nextProps.adjustments.length > 0) {
        return { ...base, ...initFromProps() };
      }
      return base;
    }

    // Initialise rows and adjustments from props on first load.
    if (!prevState._initialised && (nextProps.rows.length > 0 || nextProps.adjustments.length > 0)) {
      return initFromProps();
    }

    // Post-save re-sync: re-initialises from freshly fetched server data so that
    // newly-created adjustment IDs are picked up.
    if (prevState._syncNeeded && nextProps.qSheetId !== null) {
      return initFromProps();
    }

    // Keep attachments in sync with props (upload/delete go through the server).
    if (prevState.attachments !== nextProps.attachments) {
      return { attachments: nextProps.attachments };
    }
    return null;
  }

  state = {
    _initialised: false,
    _qSheetId: null,
    _syncNeeded: false,
    sortColumn: { path: "reportDate", order: "asc" },
    rows: [],
    adjustments: [],
    attachments: [],
    imagePreview: null,   // { src, fileName } when a preview modal is open
    notes: "",
    ccoCheck: false,
    cco: { no: "", amount: "", sign: 1 },
    adjustmentModal: MODAL_EMPTY,
    uploading: false,
    uploadProgress: { current: 0, total: 0 },
    skippedFiles: [],
    alerts: { showFileSizeError: false, showUploadError: false },
  };

  // ── Adjustment modal handlers ────────────────────────────────────────────

  handleOpenAdjModal = (editIndex = null) => {
    if (editIndex !== null) {
      const adj = this.state.adjustments[editIndex];
      this.setState({
        adjustmentModal: {
          open: true,
          editIndex,
          amount: adj.amount,
          sign: adj.sign,
          justification: adj.justification,
          error: "",
        },
      });
    } else {
      this.setState({ adjustmentModal: { ...MODAL_EMPTY, open: true } });
    }
  };

  handleCloseAdjModal = () => {
    this.setState((prev) => ({
      adjustmentModal: { ...prev.adjustmentModal, open: false, error: "" },
    }));
  };

  handleAdjModalSave = () => {
    const { adjustmentModal, adjustments } = this.state;
    const { amount, sign, justification, editIndex } = adjustmentModal;
    const parsedAmount = parseFloat(amount) || 0;

    if (parsedAmount !== 0 && !justification.trim()) {
      this.setState((prev) => ({
        adjustmentModal: {
          ...prev.adjustmentModal,
          error: "Justification is required when an amount is entered.",
        },
      }));
      return;
    }

    if (parsedAmount === 0) {
      this.setState((prev) => ({
        adjustmentModal: { ...prev.adjustmentModal, error: "Please enter a non-zero amount." },
      }));
      return;
    }

    const newAdj = {
      qSheetAdjustmentId: editIndex !== null ? adjustments[editIndex].qSheetAdjustmentId : null,
      amount: String(parsedAmount),
      sign,
      justification: justification.trim(),
    };

    const newAdjustments =
      editIndex !== null
        ? adjustments.map((a, i) => (i === editIndex ? newAdj : a))
        : [...adjustments, newAdj];

    this.setState({
      adjustments: newAdjustments,
      adjustmentModal: MODAL_EMPTY,
    });
  };

  handleDeleteAdjustment = (index) => {
    this.setState((prev) => ({
      adjustments: prev.adjustments.filter((_, i) => i !== index),
    }));
  };

  // ── Other handlers ──────────────────────────────────────────────────────

  handleChangeNotes = (e) => {
    this.setState({ notes: e.target.value });
  };

  handleChange_ccoCheck = (e) => {
    this.setState({ ccoCheck: e.target.checked });
  };

  handleChangeCCONo = (value) => {
    this.setState((prev) => ({ cco: { ...prev.cco, no: value } }));
  };

  handleChangeCCOAmount = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, "").replace(/(\.){2,}/g, ".");
    this.setState((prev) => ({ cco: { ...prev.cco, amount: cleaned } }));
  };

  handleChangeCCOSign = (sign) => {
    this.setState((prev) => ({ cco: { ...prev.cco, sign } }));
  };

  handleSort = (sortColumn) => this.setState({ sortColumn });

  // ── Attachment handlers ──────────────────────────────────────────────────

  handleUploadClick = () => {
    this.fileInputRef.current.click();
  };

  handleFileSelected = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;

    // Duplicate detection — abort if any selected file name already exists
    const existingNames = new Set(this.state.attachments.map((a) => a.fileName));
    if (files.some((f) => existingNames.has(f.name))) {
      alert(`One or more selected files already exist as attachments. Please rename the file(s) and try again.`);
      return;
    }

    this.setState({
      uploading: true,
      uploadProgress: { current: 0, total: files.length },
      skippedFiles: [],
    });

    const skippedFiles = [];
    let uploadedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.setState({ uploadProgress: { current: i + 1, total: files.length } });

      try {
        await this.props.onUploadAttachment?.(file);
        uploadedCount++;
      } catch (err) {
        const isFileSizeError =
          err?.response?.status === 413 ||
          err?.type?.includes("BadHttpRequestException") ||
          err?.message?.toLowerCase().includes("request body too large") ||
          err?.message?.toLowerCase().includes("max request body size");

        if (isFileSizeError) {
          skippedFiles.push(file.name);
          continue;
        }

        // Non-size error — stop the batch and show error alert
        this.setState((prev) => ({
          uploading: false,
          uploadProgress: { current: 0, total: 0 },
          skippedFiles,
          alerts: { ...prev.alerts, showUploadError: true },
        }));
        return;
      }
    }

    this.setState((prev) => ({
      uploading: false,
      uploadProgress: { current: 0, total: 0 },
      skippedFiles,
      alerts: skippedFiles.length > 0
        ? { ...prev.alerts, showFileSizeError: true }
        : prev.alerts,
    }));

    if (uploadedCount > 0) {
      this.props.onUploadsDone?.();
    }
  };

  handleDeleteAttachment = (attachmentId) => {
    this.props.onDeleteAttachment?.(attachmentId);
  };

  toggleAlert_fileSizeError = () =>
    this.setState((prev) => ({
      alerts: { ...prev.alerts, showFileSizeError: !prev.alerts.showFileSizeError },
      skippedFiles: [],
    }));

  toggleAlert_uploadError = () =>
    this.setState((prev) => ({
      alerts: { ...prev.alerts, showUploadError: !prev.alerts.showUploadError },
    }));

  isImage = (fileName) => /\.(jpg|jpeg|png)$/i.test(fileName);

  handlePreviewOpen = (qSheetId, attachmentId, fileName) => {
    this.setState({
      imagePreview: {
        src: `/api/QSheets/${qSheetId}/Attachments/${attachmentId}`,
        fileName,
      },
    });
  };

  handlePreviewClose = () => this.setState({ imagePreview: null });

  handleDownloadAttachment = (qSheetId, attachmentId, fileName) => {
    const link = document.createElement("a");
    link.href = `/api/QSheets/${qSheetId}/Attachments/${attachmentId}`;
    link.download = fileName;
    link.click();
  };

  // Called by the parent (via ref) after a save+fetch completes so that
  // getDerivedStateFromProps will re-sync local state from the fresh server data.
  refreshFromServer = () => this.setState({ _syncNeeded: true });

  getSaveData = () => {
    const { adjustments, notes, ccoCheck } = this.state;
    return {
      adjustments: adjustments
        .filter((a) => (parseFloat(a.amount) || 0) !== 0 && a.justification.trim())
        .map((a) => ({
          qSheetAdjustmentId: a.qSheetAdjustmentId || null,
          adjustmentAmount: (parseFloat(a.amount) || 0) * a.sign,
          justification: a.justification,
        })),
      notesRemarks: notes,
      payQuantityIncludesCcoWork: ccoCheck,
    };
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.rows !== this.state.rows || prevState.adjustments !== this.state.adjustments) {
      const { rows, adjustments } = this.state;
      const reportSum = rows.reduce((sum, r) => sum + (parseFloat(r.estimatedQuantity) || 0), 0);
      const adjustmentTotal = adjustments.reduce(
        (sum, a) => sum + (parseFloat(a.amount) || 0) * a.sign,
        0
      );
      this.props.onDataChange?.({ reportSum, adjustmentTotal });
    }
  }

  // ── Columns ──────────────────────────────────────────────────────────────

  getColumns = () => [
    {
      path: "reportDate",
      label: "Date",
      content: (report) => formatDate(report.reportDate),
      thClassName: "Q-table__date",
      tdClassName: "Q-table__date",
    },
    {
      path: "reportNumber",
      disableSort: true,
      label: "Report #",
      content: (report) => (
        <a
          href="#open-adr"
          onClick={(e) => {
            e.preventDefault();
            actionScripts.FETCH_ADR.run(this.props.projectId, report.assistantDailyReportId);
          }}
        >
          {report.reportNumber}
        </a>
      ),
      thClassName: "Q-table__reportNo",
      tdClassName: "Q-table__reportNo",
    },
    {
      path: "locationName",
      disableSort: true,
      label: "Location",
      content: (report) => report.locationName || "",
      thClassName: "Q-table__location",
      tdClassName: "Q-table__location",
    },
    {
      path: "author",
      label: "Author",
      content: (report) => report.author,
      thClassName: "Q-table__author",
      tdClassName: "Q-table__author",
    },
    {
      path: "estimatedQuantity",
      disableSort: true,
      label: "Est. Qty Reported",
      content: (report) =>
        report.estimatedQuantity != null
          ? formatQty(parseFloat(report.estimatedQuantity))
          : "",
      thClassName: "Q-table__quantity",
      tdClassName: "Q-table__quantity",
    },
  ];

  render() {
    const { quantitiesReadOnly: qSheetReadOnly, adjustmentReadOnly, submittedAdrCount } = this.props;
    const { sortColumn, rows, adjustments, notes, attachments, imagePreview, cco, ccoCheck, adjustmentModal, uploading, uploadProgress, skippedFiles, alerts } = this.state;

    const unitOfMeasure = this.props.unitOfMeasure || "";
    const reportSum = rows.reduce((sum, r) => sum + (parseFloat(r.estimatedQuantity) || 0), 0);
    const adjustmentTotal = adjustments.reduce(
      (sum, a) => sum + (parseFloat(a.amount) || 0) * a.sign,
      0
    );
    const finalTotal = reportSum + adjustmentTotal;

    const signButtonStyle = { width: "28px", height: "28px", padding: 0 };
    const modalAmountValid = (parseFloat(adjustmentModal.amount) || 0) !== 0;

    return (
      <React.Fragment>

        {/* ── Quantities ─────────────────────────────────────────────── */}
        <div className="card m-2">
          <div className="card-body">
            <h5 className="mb-2">Daily Report Estimated Quantities</h5>
            {submittedAdrCount > 0 && (
              <div className="alert alert-danger mb-2 mt-2 py-2 px-3 text-dark">
                <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                There {submittedAdrCount === 1 ? "is" : "are"}{" "}
                <a
                  href="#open-adr-list"
                  className="alert-link text-dark"
                  onClick={(e) => {
                    e.preventDefault();
                    actionScripts.OPEN_ADR_LIST.run(this.props.projectId);
                  }}
                >
                  <strong>{submittedAdrCount}</strong> daily report{submittedAdrCount === 1 ? "" : "s"}
                </a>{" "}
                for this item that {submittedAdrCount === 1 ? "has" : "have"} not been accepted yet for the pay period and will not be included.
              </div>
            )}
            <div className="qSheet-table-container border">
              <Table
                className="table table-sm table-hover qSheet-table w-100"
                data={rows}
                idPropName="qSheetRowId"
                columns={this.getColumns()}
                sortColumn={sortColumn}
                onSort={this.handleSort}
                handleClick_tr={() => {}}
              />
            </div>
            <div className="d-flex align-items-center mt-2">
              <span className="ml-auto mr-3 text-right">Sum of Report Est. Quantities:</span>
              <strong className="Q-value-col">{formatQty(reportSum)} {unitOfMeasure}</strong>
            </div>
          </div>
        </div>

        {/* ── Adjustments ────────────────────────────────────────────── */}
        <div className="card m-2">
          <div className="card-body">
            <h5 className="mb-3">Adjustments to Estimated Quantity</h5>

            {adjustments.length === 0 && (
              <p className="text-muted small mb-2">No adjustments.</p>
            )}

            {adjustments.map((adj, index) => {
              const signed = (parseFloat(adj.amount) || 0) * adj.sign;
              const isNeg = adj.sign === -1;
              return (
                <div
                  key={adj.qSheetAdjustmentId ?? `pending-${index}`}
                  className="d-flex align-items-start mb-2 pb-2 border-bottom"
                >
                  {/* Justification + attribution */}
                  <div className="flex-grow-1 min-width-0">
                    <span>{adj.justification}</span>
                    {adj.createdByFullName && (
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        Added by {adj.createdByFullName}
                        {adj.createdOn ? ` on ${formatDate(adj.createdOn)}` : ""}
                        {adj.lastEditedByFullName && (
                          <span>
                            {" – "}Last edited by {adj.lastEditedByFullName}
                            {adj.lastEditedOn ? ` on ${formatDate(adj.lastEditedOn)}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Sign + value */}
                  <div className="Q-adj-value-col d-flex align-items-center flex-shrink-0">
                    <span
                      className={`font-weight-bold mr-1 flex-shrink-0 ${isNeg ? "text-danger" : "text-success"}`}
                    >
                      {isNeg ? "−" : "+"}
                    </span>
                    <strong>{adj.amount} {unitOfMeasure}</strong>
                  </div>
                  {/* Action buttons at far end */}
                  {!adjustmentReadOnly && (
                    <div className="d-flex flex-shrink-0 ml-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-secondary p-0 mr-2"
                        title="Edit adjustment"
                        onClick={() => this.handleOpenAdjModal(index)}
                      >
                        <FontAwesomeIcon icon="edit" className="fa-fw" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0"
                        title="Remove adjustment"
                        onClick={() => this.handleDeleteAdjustment(index)}
                      >
                        <FontAwesomeIcon icon="times" className="fa-fw" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="d-flex align-items-center">
              {!adjustmentReadOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mr-auto"
                  onClick={() => this.handleOpenAdjModal()}
                >
                  <FontAwesomeIcon icon="plus" className="fa-fw" />
                  Add Adjustment
                </button>
              )}
              {adjustments.length > 0 && (
                <>
                  <span className="ml-auto mr-3 text-right">Total Adjustment:</span>
                  <strong className="Q-value-col">{formatQty(adjustmentTotal)} {unitOfMeasure}</strong>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Total Pay ──────────────────────────────────────────────── */}
        <div className="card m-2 bg-primary text-white">
          <div className="card-body py-1">
            <div className="d-flex align-items-center">
              <h5 className="ml-auto mr-3 mb-0">Pay This Estimate:</h5>
              <h5 className="Q-value-col mb-0"><b>{formatQty(finalTotal)} {unitOfMeasure}</b></h5>
            </div>
          </div>
        </div>

        {/* ── CCO section ────────────────────────────────────────────── */}
        <div className="card m-2">
          <div className="card-body">
            <div className="row">
              <div className="custom-control custom-checkbox ml-3 w-100">
                <input
                  type="checkbox"
                  className="custom-control-input my-auto"
                  name="ccoCheckbox"
                  id="ccoCheckbox"
                  checked={ccoCheck}
                  onChange={this.handleChange_ccoCheck}
                />
                <label className="custom-control-label" htmlFor="ccoCheckbox">
                  Above Quantity includes CCO work
                </label>
              </div>
            </div>
            <Collapse isOpen={ccoCheck || false}>
              <div className="row d-flex align-items-center mt-4">
                <label className="col-2 col-form-label col-form-label-sm">
                  <b>CCO No:</b>
                </label>
                <div className="col-3">
                  <input
                    type="text"
                    name="ccoNo"
                    id="ccoNo"
                    value={cco.no}
                    onChange={(e) => this.handleChangeCCONo(e.target.value)}
                    className="form-control form-control-sm"
                  />
                </div>
                <label className="col-2 col-form-label col-form-label-sm">
                  <b>Quantity:</b>
                </label>
                <ButtonGroup className="flex-shrink-0">
                  <Button
                    size="sm" outline color="dark" active={cco.sign === 1}
                    onClick={() => this.handleChangeCCOSign(1)} style={signButtonStyle}
                  >
                    <FontAwesomeIcon icon="plus" />
                  </Button>
                  <Button
                    size="sm" outline color="dark" active={cco.sign === -1}
                    onClick={() => this.handleChangeCCOSign(-1)} style={signButtonStyle}
                  >
                    <FontAwesomeIcon icon="minus" />
                  </Button>
                </ButtonGroup>
                <div className="col pl-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={cco.amount}
                    onChange={(e) => this.handleChangeCCOAmount(e.target.value)}
                    placeholder="Amount"
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>

        {/* ── Attachments ────────────────────────────────────────────── */}
        <div className="card m-2">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Attachments</h5>
              {!qSheetReadOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={this.handleUploadClick}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon="paperclip" className="fa-fw" />
                  Upload Document
                </button>
              )}
            </div>

            <input
              ref={this.fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              accept=".docx,.xlsx,.txt,.csv,.pdf,.jpg,.jpeg,.png"
              onChange={this.handleFileSelected}
            />

            {uploading && (
              <div className="d-flex align-items-center text-muted small mb-2">
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                Uploading file {uploadProgress.current} of {uploadProgress.total}…
              </div>
            )}

            {attachments.length === 0 && !uploading && (
              <p className="text-muted small mb-0">No attachments.</p>
            )}

            {attachments.map((attachment) => {
              const imgSrc = this.isImage(attachment.fileName)
                ? `/api/QSheets/${attachment.qSheetId}/Attachments/${attachment.qSheetAttachmentId}`
                : null;
              return (
                <div key={attachment.qSheetAttachmentId} className="d-flex align-items-center mb-1">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={attachment.fileName}
                      title="Click to preview"
                      style={{ height: "36px", width: "36px", objectFit: "cover", cursor: "pointer" }}
                      className="rounded mr-2 border"
                      onClick={() => this.handlePreviewOpen(
                        attachment.qSheetId, attachment.qSheetAttachmentId, attachment.fileName
                      )}
                    />
                  ) : (
                    <FontAwesomeIcon icon="file-alt" className="fa-fw mr-2 text-muted" />
                  )}
                  <span className="flex-grow-1">{attachment.fileName}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-secondary p-0 mr-2"
                    onClick={() => this.handleDownloadAttachment(
                      attachment.qSheetId, attachment.qSheetAttachmentId, attachment.fileName
                    )}
                  >
                    <FontAwesomeIcon icon="download" className="fa-fw" />
                    Download
                  </button>
                  {!qSheetReadOnly && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      title="Remove attachment"
                      onClick={() => this.handleDeleteAttachment(attachment.qSheetAttachmentId)}
                    >
                      <FontAwesomeIcon icon="times" className="fa-fw" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Notes / Remarks ────────────────────────────────────────── */}
        <div className="card m-2">
          <div className="card-body">
            <label className="col-form-label col-form-label-sm pt-0"><b>Notes / Remarks:</b></label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              name="notes"
              id="notes"
              value={notes}
              onChange={this.handleChangeNotes}
              disabled={qSheetReadOnly}
            />
          </div>
        </div>

        {/* ── Image Preview Modal ─────────────────────────────────────── */}
        {imagePreview && (
          <Modal isOpen toggle={this.handlePreviewClose} size="lg" fade={false}>
            <ModalHeader toggle={this.handlePreviewClose}>
              {imagePreview.fileName}
            </ModalHeader>
            <ModalBody className="text-center p-2">
              <img
                src={imagePreview.src}
                alt={imagePreview.fileName}
                style={{ maxWidth: "100%", maxHeight: "75vh" }}
              />
            </ModalBody>
          </Modal>
        )}

        {/* ── File Size Error Alert ───────────────────────────────────── */}
        <Modal isOpen={alerts.showFileSizeError} toggle={this.toggleAlert_fileSizeError} fade={false}>
          <ModalHeader toggle={this.toggleAlert_fileSizeError}>File Too Large</ModalHeader>
          <ModalBody>
            <p>The following file(s) exceeded the size limit and were not uploaded:</p>
            <ul className="mb-0">
              {skippedFiles.map((name) => (
                <li key={name}><strong>{name}</strong></li>
              ))}
            </ul>
            <p className="mt-2 mb-0 text-muted small">Maximum file size is 30 MB.</p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.toggleAlert_fileSizeError}>OK</Button>
          </ModalFooter>
        </Modal>

        {/* ── Upload Error Alert ──────────────────────────────────────── */}
        <Modal isOpen={alerts.showUploadError} toggle={this.toggleAlert_uploadError} fade={false}>
          <ModalHeader toggle={this.toggleAlert_uploadError}>Upload Failed</ModalHeader>
          <ModalBody>
            An error occurred while uploading. Please try again or contact the system administrator.
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.toggleAlert_uploadError}>OK</Button>
          </ModalFooter>
        </Modal>

        {/* ── Adjustment Modal ────────────────────────────────────────── */}
        <Modal isOpen={adjustmentModal.open} toggle={this.handleCloseAdjModal} fade={false}>
          <ModalHeader toggle={this.handleCloseAdjModal}>
            {adjustmentModal.editIndex !== null ? "Edit Adjustment" : "Add Adjustment"}
          </ModalHeader>
          <ModalBody>

            {/* Amount */}
            <div className="form-group">
              <label className="col-form-label col-form-label-sm pt-0">
                <b>Amount <span className="text-danger">*</span></b>
              </label>
              <div className="d-flex align-items-center">
                <ButtonGroup className="mr-2 flex-shrink-0">
                  <Button
                    size="sm" outline color="dark"
                    active={adjustmentModal.sign === 1}
                    onClick={() =>
                      this.setState((prev) => ({
                        adjustmentModal: { ...prev.adjustmentModal, sign: 1, error: "" },
                      }))
                    }
                    style={signButtonStyle}
                  >
                    <FontAwesomeIcon icon="plus" />
                  </Button>
                  <Button
                    size="sm" outline color="dark"
                    active={adjustmentModal.sign === -1}
                    onClick={() =>
                      this.setState((prev) => ({
                        adjustmentModal: { ...prev.adjustmentModal, sign: -1, error: "" },
                      }))
                    }
                    style={signButtonStyle}
                  >
                    <FontAwesomeIcon icon="minus" />
                  </Button>
                </ButtonGroup>
                <input
                  type="text"
                  className={`form-control form-control-sm ${adjustmentModal.error && !modalAmountValid ? "is-invalid" : ""}`}
                  value={adjustmentModal.amount}
                  onChange={(e) => {
                    let cleaned = e.target.value.replace(/[^0-9.]/g, "").replace(/(\.){2,}/g, ".");
                    const parts = cleaned.split(".");
                    if (parts.length > 1 && parts[1].length > 3) {
                      cleaned = parts[0] + "." + parts[1].substring(0, 3);
                    }
                    this.setState((prev) => ({
                      adjustmentModal: { ...prev.adjustmentModal, amount: cleaned, error: "" },
                    }));
                  }}
                  placeholder="0.000"
                  autoFocus
                />
                {unitOfMeasure && (
                  <span className="ml-2 text-muted text-nowrap">{unitOfMeasure}</span>
                )}
              </div>
            </div>

            {/* Justification */}
            <div className="form-group mb-0">
              <label className="col-form-label col-form-label-sm pt-0">
                <b>Justification <span className="text-danger">*</span></b>
              </label>
              <textarea
                className={`form-control form-control-sm ${adjustmentModal.error && modalAmountValid && !adjustmentModal.justification.trim() ? "is-invalid" : ""}`}
                rows={3}
                value={adjustmentModal.justification}
                onChange={(e) =>
                  this.setState((prev) => ({
                    adjustmentModal: { ...prev.adjustmentModal, justification: e.target.value, error: "" },
                  }))
                }
                placeholder="Required"
              />
              {adjustmentModal.error && (
                <div className="invalid-feedback d-block">
                  <FontAwesomeIcon icon="exclamation-circle" className="mr-1" />
                  {adjustmentModal.error}
                </div>
              )}
            </div>

          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={this.handleCloseAdjModal}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.handleAdjModalSave}>
              {adjustmentModal.editIndex !== null ? "Update" : "Add"}
            </Button>
          </ModalFooter>
        </Modal>

      </React.Fragment>
    );
  }
}

export default QBody;
